// Mock data for the "PO Level Detail View" IFP report.
// The 7 anchor rows below are transcribed verbatim from the reference
// screenshot (top 7 rows by Dollars Shifted) so the default ("All" filters,
// "Latest" snapshot) render matches exactly, including the odd static "0"
// Days Shifted total row (a faithfully-reproduced Power BI aggregation
// quirk — not a real sum). Filler rows are deterministically generated to
// round out the table without disturbing the anchors' top-7 ranking.

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

export interface PoLevelDetailRow {
  po: string
  division: string
  department: string
  className: string
  subclass: string
  sku: string
  dc: string
  origArrWk: string
  estimatedArrWk: string
  daysShifted: number
  dollarsShifted: number
  unitsShifted: number
  event: string
}

export type Movement = 'Acceleration' | 'Delay'

export function movementFor(row: PoLevelDetailRow): Movement {
  return row.estimatedArrWk < row.origArrWk ? 'Acceleration' : 'Delay'
}

interface HierarchyCombo {
  division: string
  department: string
  className: string
  subclass: string
}

const HIERARCHY_COMBOS: HierarchyCombo[] = [
  {
    division: '1-CrAFTSY',
    department: '41-STITCHERY',
    className: '532-SINGER SEWING MACHINES',
    subclass: '41-SINGER SM',
  },
  {
    division: '10-CELEBRATIONS/SEASONAL/FLORAL & DECOR',
    department: '28-CMAS TREES',
    className: '41-PENCIL TREES',
    subclass: "132-7' PROMO CASHMERE PENCIL",
  },
  {
    division: '1-CrAFTSY',
    department: '70-PAINTING ART SUPPLIES',
    className: '66-PAINTING ACCESSORIES',
    subclass: '9120-IWATA MACHINES AND ACCESSORIES',
  },
  {
    division: '1-CrAFTSY',
    department: '40-YARN',
    className: '686-CHENILLE YARN',
    subclass: '1813-BER BLANKET BIG',
  },
  // Synthetic extras — not visible in the screenshot, used only to give the
  // filler rows some hierarchy variety.
  {
    division: '1-CrAFTSY',
    department: '45-NEEDLEARTS',
    className: '210-YARN CRAFTS',
    subclass: '15-KNITTING KITS',
  },
  {
    division: '10-CELEBRATIONS/SEASONAL/FLORAL & DECOR',
    department: '31-EVERYDAY FLORAL',
    className: '88-STEMS & BUSHES',
    subclass: '22-SILK STEMS',
  },
]

// The 7 top-by-Dollars-Shifted rows, transcribed exactly from the screenshot.
const ANCHOR_ROWS: PoLevelDetailRow[] = [
  {
    po: '41611604',
    division: '1-CrAFTSY',
    department: '41-STITCHERY',
    className: '532-SINGER SEWING MACHINES',
    subclass: '41-SINGER SM',
    sku: '10832606',
    dc: '1_Dallas',
    origArrWk: '202626',
    estimatedArrWk: '202624',
    daysShifted: 16,
    dollarsShifted: 348130,
    unitsShifted: 1021,
    event: 'SEA03',
  },
  {
    po: '41032129',
    division: '10-CELEBRATIONS/SEASONAL/FLORAL & DECOR',
    department: '28-CMAS TREES',
    className: '41-PENCIL TREES',
    subclass: "132-7' PROMO CASHMERE PENCIL",
    sku: '10772927',
    dc: '3_Memphis',
    origArrWk: '202633',
    estimatedArrWk: '202631',
    daysShifted: 17,
    dollarsShifted: 285585,
    unitsShifted: 9694,
    event: 'SEA07',
  },
  {
    po: '41468367',
    division: '1-CrAFTSY',
    department: '41-STITCHERY',
    className: '532-SINGER SEWING MACHINES',
    subclass: '41-SINGER SM',
    sku: '10832611',
    dc: '1_Dallas',
    origArrWk: '202626',
    estimatedArrWk: '202629',
    daysShifted: 21,
    dollarsShifted: 235107,
    unitsShifted: 1592,
    event: 'SEA03',
  },
  {
    po: '41026826',
    division: '10-CELEBRATIONS/SEASONAL/FLORAL & DECOR',
    department: '28-CMAS TREES',
    className: '41-PENCIL TREES',
    subclass: "3368-7' PROMO PENCIL TREE",
    sku: '10772923',
    dc: '3_Memphis',
    origArrWk: '202633',
    estimatedArrWk: '202631',
    daysShifted: 17,
    dollarsShifted: 226350,
    unitsShifted: 10060,
    event: 'SEA07',
  },
  {
    po: '41121326',
    division: '1-CrAFTSY',
    department: '70-PAINTING ART SUPPLIES',
    className: '66-PAINTING ACCESSORIES',
    subclass: '9120-IWATA MACHINES AND ACCESSORIES',
    sku: '10823706',
    dc: '3_Memphis',
    origArrWk: '202624',
    estimatedArrWk: '202625',
    daysShifted: 3,
    dollarsShifted: 163271,
    unitsShifted: 0,
    event: 'SEA01',
  },
  {
    po: '41228831',
    division: '1-CrAFTSY',
    department: '40-YARN',
    className: '686-CHENILLE YARN',
    subclass: '1813-BER BLANKET BIG',
    sku: '10822079',
    dc: '4_Indianapolis',
    origArrWk: '202626',
    estimatedArrWk: '202629',
    daysShifted: 20,
    dollarsShifted: 148612,
    unitsShifted: 21984,
    event: 'SEA05',
  },
  {
    po: '41121322',
    division: '1-CrAFTSY',
    department: '70-PAINTING ART SUPPLIES',
    className: '66-PAINTING ACCESSORIES',
    subclass: '9120-IWATA MACHINES AND ACCESSORIES',
    sku: '10823706',
    dc: '5_Los Angeles',
    origArrWk: '202624',
    estimatedArrWk: '202625',
    daysShifted: 3,
    dollarsShifted: 144932,
    unitsShifted: 0,
    event: 'SEA01',
  },
]

const SMALLEST_ANCHOR_DOLLARS = Math.min(
  ...ANCHOR_ROWS.map(r => r.dollarsShifted)
)

const FILLER_ROW_COUNT = 50

function buildFillerRow(index: number): PoLevelDetailRow {
  const id = `po-filler-${index}`
  const seed = hashString(id)
  const combo = HIERARCHY_COMBOS[index % HIERARCHY_COMBOS.length]

  const origWeek = 23 + Math.floor(normalizedFromHash(seed, 'origWeek') * 18) // 23-40
  const weekDiffMagnitude = 1 + Math.floor(normalizedFromHash(seed, 'diff') * 4) // 1-4
  const weekDiffSign = normalizedFromHash(seed, 'diffSign') < 0.5 ? -1 : 1
  const estWeek = origWeek + weekDiffMagnitude * weekDiffSign

  const jitter = Math.round((normalizedFromHash(seed, 'daysJitter') - 0.5) * 4) // +-2
  const daysShifted = Math.max(1, weekDiffMagnitude * 7 + jitter)

  const eventNumber = 1 + Math.floor(normalizedFromHash(seed, 'event') * 10)
  const dcOptions = [
    '1_Dallas',
    '2_Atlanta',
    '3_Memphis',
    '4_Indianapolis',
    '5_Los Angeles',
  ]
  const dc =
    dcOptions[Math.floor(normalizedFromHash(seed, 'dc') * dcOptions.length)]

  const dollarsShifted = Math.round(
    5000 +
      normalizedFromHash(seed, 'dollars') * (SMALLEST_ANCHOR_DOLLARS - 5500)
  )
  const unitsShifted = Math.round(normalizedFromHash(seed, 'units') * 15000)

  return {
    po: String(40000000 + (hashString(id + 'po') % 9999999)),
    division: combo.division,
    department: combo.department,
    className: combo.className,
    subclass: combo.subclass,
    sku: String(10000000 + (hashString(id + 'sku') % 89999999)),
    dc,
    origArrWk: `2026${String(origWeek).padStart(2, '0')}`,
    estimatedArrWk: `2026${String(estWeek).padStart(2, '0')}`,
    daysShifted,
    dollarsShifted,
    unitsShifted,
    event: `SEA${String(eventNumber).padStart(2, '0')}`,
  }
}

const FILLER_ROWS: PoLevelDetailRow[] = Array.from(
  { length: FILLER_ROW_COUNT },
  (_, i) => buildFillerRow(i)
)

export const ALL_PO_ROWS: PoLevelDetailRow[] = [...ANCHOR_ROWS, ...FILLER_ROWS]

export const EVENT_OPTIONS: string[] = Array.from(
  new Set(ALL_PO_ROWS.map(r => r.event))
).sort()

export const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]

export const ORIG_ARR_WK_OPTIONS: string[] = Array.from(
  new Set(ALL_PO_ROWS.map(r => r.origArrWk))
).sort()

export const ESTIMATED_ARR_WK_OPTIONS: string[] = Array.from(
  new Set(ALL_PO_ROWS.map(r => r.estimatedArrWk))
).sort()

export const PO_OPTIONS: string[] = Array.from(
  new Set(ALL_PO_ROWS.map(r => r.po))
).sort()

// Fixed anchor so the option list is stable across renders/sessions rather
// than depending on the actual current date.
const SNAPSHOT_ANCHOR = new Date('2026-07-14')

function lastFridayOnOrBefore(date: Date): Date {
  const next = new Date(date)
  const day = next.getDay() // 0=Sun...6=Sat, Fri=5
  const diff = (day - 5 + 7) % 7
  next.setDate(next.getDate() - diff)
  return next
}

function formatSnapshotDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

const RECENT_FRIDAYS: string[] = (() => {
  const fridays: string[] = []
  let cursor = lastFridayOnOrBefore(SNAPSHOT_ANCHOR)
  for (let i = 0; i < 4; i++) {
    fridays.push(formatSnapshotDate(cursor))
    cursor = new Date(cursor)
    cursor.setDate(cursor.getDate() - 7)
  }
  return fridays
})()

export const SNAPSHOT_DATE_OPTIONS = ['Latest', ...RECENT_FRIDAYS]

export interface PoLevelDetailFilterState {
  snapshotDate: string
  season: string | null
  event: string | null
  divisionId: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  dc: string | null
  origArrWk: string | null
  estimatedArrWk: string | null
  po: string | null
}

function shrinkForFilter(name: string, value: string): number {
  const norm = normalizedFromHash(hashString(`${name}:${value}`), 'shrink')
  return 0.4 + norm * 0.35 // [0.4, 0.75]
}

function cosmeticFilterPairs(
  filters: PoLevelDetailFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.season) pairs.push(['season', filters.season])
  if (filters.divisionId) pairs.push(['division', filters.divisionId])
  if (filters.departmentId) pairs.push(['department', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.snapshotDate !== 'Latest')
    pairs.push(['snapshotDate', filters.snapshotDate])
  return pairs
}

function matchesRowFilters(
  row: PoLevelDetailRow,
  filters: PoLevelDetailFilterState
): boolean {
  if (filters.event && row.event !== filters.event) return false
  if (filters.dc && row.dc !== filters.dc) return false
  if (filters.origArrWk && row.origArrWk !== filters.origArrWk) return false
  if (filters.estimatedArrWk && row.estimatedArrWk !== filters.estimatedArrWk)
    return false
  if (filters.po && row.po !== filters.po) return false
  return true
}

interface BuildPoLevelDetailRowsOptions {
  ensureDrillResults?: boolean
}

function weekDistance(from: string, to: string): number {
  const fromYear = Number(from.slice(0, 4))
  const fromWeek = Number(from.slice(4))
  const toYear = Number(to.slice(0, 4))
  const toWeek = Number(to.slice(4))

  if (
    !Number.isFinite(fromYear) ||
    !Number.isFinite(fromWeek) ||
    !Number.isFinite(toYear) ||
    !Number.isFinite(toWeek)
  ) {
    return 1
  }

  return Math.max(1, Math.abs((toYear - fromYear) * 52 + toWeek - fromWeek))
}

function buildFallbackDrillRows(
  filters: PoLevelDetailFilterState
): PoLevelDetailRow[] {
  if (!filters.origArrWk || !filters.estimatedArrWk) return []

  const contextKey = [
    filters.snapshotDate,
    filters.season,
    filters.event,
    filters.divisionId,
    filters.departmentId,
    filters.classId,
    filters.subclassId,
    filters.dc,
    filters.origArrWk,
    filters.estimatedArrWk,
  ].join('|')
  const contextSeed = hashString(`drill:${contextKey}`)
  const rowCount = 4 + (contextSeed % 4)
  const shiftedWeeks = weekDistance(filters.origArrWk, filters.estimatedArrWk)

  return Array.from({ length: rowCount }, (_, index) => {
    const seed = hashString(`${contextKey}:row:${index}`)
    const combo =
      HIERARCHY_COMBOS[
        Math.floor(
          normalizedFromHash(seed, 'hierarchy') * HIERARCHY_COMBOS.length
        )
      ]
    const eventNumber = 1 + Math.floor(normalizedFromHash(seed, 'event') * 10)
    const dc =
      DC_OPTIONS[Math.floor(normalizedFromHash(seed, 'dc') * DC_OPTIONS.length)]

    return {
      po: String(
        40000000 + (hashString(`${contextKey}:po:${index}`) % 9999999)
      ),
      division: combo.division,
      department: combo.department,
      className: combo.className,
      subclass: combo.subclass,
      sku: String(
        10000000 + (hashString(`${contextKey}:sku:${index}`) % 89999999)
      ),
      dc: filters.dc ?? dc,
      origArrWk: filters.origArrWk!,
      estimatedArrWk: filters.estimatedArrWk!,
      daysShifted: Math.max(
        1,
        shiftedWeeks * 7 +
          Math.round((normalizedFromHash(seed, 'days') - 0.5) * 4)
      ),
      dollarsShifted: Math.round(
        25000 + normalizedFromHash(seed, 'dollars') * 275000
      ),
      unitsShifted: Math.round(250 + normalizedFromHash(seed, 'units') * 18000),
      event: filters.event ?? `SEA${String(eventNumber).padStart(2, '0')}`,
    }
  })
}

export function buildPoLevelDetailRows(
  filters: PoLevelDetailFilterState,
  options: BuildPoLevelDetailRowsOptions = {}
): PoLevelDetailRow[] {
  const cosmeticPairs = cosmeticFilterPairs(filters)
  const shrink = cosmeticPairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(name, value),
    1
  )
  const wiggleSeed = cosmeticPairs.length
    ? hashString(cosmeticPairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0

  let filtered = ALL_PO_ROWS.filter(row => matchesRowFilters(row, filters))
  if (
    filtered.length === 0 &&
    options.ensureDrillResults &&
    filters.origArrWk &&
    filters.estimatedArrWk
  ) {
    filtered = buildFallbackDrillRows(filters).filter(row =>
      matchesRowFilters(row, filters)
    )
  }

  const rows = filtered.map(row => {
    const dollarsShifted = Math.max(0, Math.round(row.dollarsShifted * shrink))
    const unitsShifted = Math.max(0, Math.round(row.unitsShifted * shrink))
    const daysJitter = wiggleSeed
      ? Math.round((normalizedFromHash(wiggleSeed, row.po) - 0.5) * 5) // +-2/3
      : 0
    const daysShifted = Math.max(0, row.daysShifted + daysJitter)

    return {
      ...row,
      dollarsShifted,
      unitsShifted,
      daysShifted,
    }
  })

  return rows.sort((a, b) => b.dollarsShifted - a.dollarsShifted)
}
